/**
 * Черновик монтажа — ручной набор кусков с перетаскиванием, обрезкой начала и слайдом-концовкой
 * (видео-ролик из готового кадра, §9.1–9.2 спеки `2026-10-13-frame-to-video-design.md`). Правила —
 * копии серверных (`reels/videocut.py`): после обрезки должен остаться хотя бы кадр выходной частоты
 * (`TRIM_MIN_REMAINDER_MS`, `OUT_FPS = 24`), слайд — отдельные `outro_ms`/`outro_text`
 * (`OUTRO_MS_RANGE`, `OUTRO_TEXT_MAX`), кусок идёт в ролик `min(shot_duration_ms, file_ms − trim)`.
 *
 * Без импортов из `@/…`: модуль проверяется `node --test` (tests/cutDraft.test.ts) как есть.
 */

/**
 * После обрезки начала куска должен остаться хотя бы один кадр выходной частоты 24 fps
 * (`videocut.TRIM_MIN_REMAINDER_MS = ceil(1000 / OUT_FPS)`) — меньше, и ffmpeg при рендере молча
 * роняет дробный кадр: кусок выпадает из ролика целиком без ошибки сборки.
 */
export const TRIM_MIN_REMAINDER_MS = 42;

/** Диапазон длительности слайда, мс (`videocut.OUTRO_MS_RANGE`). */
export const OUTRO_MS_RANGE: readonly [number, number] = [1000, 5000];
/** Слайд по умолчанию, когда включают галочку (`videocut.OUTRO_MS`). */
export const OUTRO_MS_DEFAULT = 1800;
/** Надпись слайда — не больше стольких знаков (`videocut.OUTRO_TEXT_MAX`). */
export const OUTRO_TEXT_MAX = 20;
/** Надпись слайда по умолчанию (`videocut.OUTRO_TEXT`). */
export const OUTRO_TEXT_DEFAULT = "UNIRENTER";

/** Кусок черновика: кадр, выбранный клип, длительность его файла и кадра, обрезка начала. */
export type CutDraftItem = {
  shot_id: number;
  variant_id: number;
  /** Длительность файла клипа, мс (`variant_video_ms`). */
  file_ms: number;
  /** Длительность кадра в ролике, мс (`shot_duration_ms`) — потолок куска. */
  shot_duration_ms: number;
  /** Обрезка начала, мс; 0 — без обрезки. */
  trim_start_ms: number;
  /** Сколько куска идёт в ролик, мс (№353); `null` — по длительности кадра, как раньше. */
  duration_ms?: number | null;
};

/** Слайд-концовка черновика: `on` — показывать ли поля (без галочки `outro_ms`/`outro_text` в тело не идут). */
export type OutroDraft = { on: boolean; ms: number; text: string };

export type CutDraft = { items: readonly CutDraftItem[]; outro: OutroDraft };

/** Порядок с элементом, переставленным с индекса `from` на индекс `to` (перетаскивание куска). */
export function reorder<T>(items: readonly T[], from: number, to: number): T[] {
  if (from < 0 || from >= items.length) return [...items];
  const copy = items.slice();
  const [moved] = copy.splice(from, 1) as [T];
  copy.splice(Math.max(0, Math.min(to, copy.length)), 0, moved);
  return copy;
}

/**
 * Обрезка начала куска: целое ≥ 0, после неё должно остаться хотя бы `minMs` файла
 * (`videocut._trim_ms`) — не пускает ни ниже нуля, ни за длину файла.
 */
export function clampTrim(start: number, fileMs: number, minMs: number = TRIM_MIN_REMAINDER_MS): number {
  const max = Math.max(0, fileMs - minMs);
  return Math.min(Math.max(0, Math.round(start)), max);
}

/**
 * Длительность куска в ролике — тот же расчёт, что у сервера (`videocut._item_ms`): без своего
 * значения берём длительность кадра, со своим — его, но не длиннее остатка клипа после обрезки.
 */
export function durationOf(
  item: Pick<CutDraftItem, "file_ms" | "shot_duration_ms" | "trim_start_ms" | "duration_ms">,
): number {
  const left = Math.max(0, item.file_ms - item.trim_start_ms);
  const want = item.duration_ms ?? null;
  return want === null ? Math.max(0, Math.min(item.shot_duration_ms, left)) : Math.max(0, Math.min(want, left));
}

/**
 * Привести свою длительность куска к текущему остатку клипа — после смены обрезки или самого клипа
 * (найдено ревью 16.10): иначе на экране показывалась обрезанная длина, а в тело уходила прежняя, и
 * сервер отказывал «длиннее кусок не вырезать».
 */
export const reclampItem = (it: CutDraftItem): CutDraftItem =>
  it.duration_ms == null ? it : { ...it, duration_ms: clampDuration(it.duration_ms, it) };

/** Длительность куска, заданная руками: целое в пределах остатка клипа (как `videocut._item_ms`). */
export function clampDuration(
  ms: number,
  item: Pick<CutDraftItem, "file_ms" | "trim_start_ms">,
  minMs: number = TRIM_MIN_REMAINDER_MS,
): number {
  const left = Math.max(minMs, item.file_ms - item.trim_start_ms);
  return Math.min(Math.max(minMs, Math.round(ms)), left);
}

/** Итоговая длина ролика: сумма кусков + слайд (0, если его нет или `outroMs` ≤ 0). */
export function sumMs(
  items: readonly Pick<CutDraftItem, "file_ms" | "shot_duration_ms" | "trim_start_ms" | "duration_ms">[],
  outroMs: number,
): number {
  return items.reduce((n, it) => n + durationOf(it), 0) + Math.max(0, outroMs);
}

/**
 * Черновик → тело `/api/projects/{id}/video/cuts` (поля `items`/`outro_ms`/`outro_text`; переход, титр,
 * музыку, грейд и знак добавляет вызывающий). `trim_start_ms` — только при ненулевой обрезке (идиома
 * `routed_from` на сервере: иначе отпечаток монтажей без обрезки изменился бы). Без включённого слайда
 * (`outro.on === false`) ключей `outro_*` в теле нет вовсе — как и у сервера, надпись без длительности
 * слайда считается негодным значением, а не тихим игнором.
 */
export function toCutBody(draft: CutDraft): {
  items: { shot_id: number; variant_id: number; trim_start_ms?: number; duration_ms?: number }[];
  outro_ms?: number;
  outro_text?: string;
} {
  return {
    items: draft.items.map((it) => {
      // Ключи шлём только когда они что-то меняют: отпечаток монтажей без обрезки и без своей
      // длительности обязан остаться прежним (решение 10 спеки «Видео-ролик из готового кадра»).
      const base: { shot_id: number; variant_id: number; trim_start_ms?: number; duration_ms?: number } = {
        shot_id: it.shot_id,
        variant_id: it.variant_id,
      };
      if (it.trim_start_ms > 0) base.trim_start_ms = it.trim_start_ms;
      if (it.duration_ms != null && it.duration_ms !== Math.min(it.shot_duration_ms, it.file_ms - it.trim_start_ms)) {
        base.duration_ms = it.duration_ms;
      }
      return base;
    }),
    ...(draft.outro.on ? { outro_ms: draft.outro.ms, outro_text: draft.outro.text } : {}),
  };
}
